import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, Flame } from "lucide-react";
import { useBanner } from "@/context/BannerContext";

const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  try {
    const end = new Date(target.replace("Z", "+00:00")).getTime();
    const diff = Math.max(0, end - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff / 3600000) % 24);
    const m = Math.floor((diff / 60000) % 60);
    const s = Math.floor((diff / 1000) % 60);
    return { d, h, m, s, done: diff === 0 };
  } catch {
    return null;
  }
};

const CountdownPill = ({ target, textColor }) => {
  const cd = useCountdown(target);
  if (!cd) return null;
  const box = "px-1.5 py-0.5 rounded-sm bg-black/20 tabular-nums text-[11px]";
  return (
    <div className="flex items-center gap-1" style={{ color: textColor }}>
      {cd.d > 0 && <span className={box}>{cd.d}d</span>}
      <span className={box}>{String(cd.h).padStart(2, "0")}h</span>
      <span className={box}>{String(cd.m).padStart(2, "0")}m</span>
      <span className={box}>{String(cd.s).padStart(2, "0")}s</span>
    </div>
  );
};

const isInternal = (href) => href && (href.startsWith("/") || href.startsWith("#"));

const CtaLink = ({ href, children, className, testId, onClick, style }) => {
  if (!href) return <button className={className} style={style} onClick={onClick} data-testid={testId}>{children}</button>;
  if (isInternal(href)) return <Link to={href} className={className} style={style} onClick={onClick} data-testid={testId}>{children}</Link>;
  return <a href={href} target="_blank" rel="noreferrer" className={className} style={style} onClick={onClick} data-testid={testId}>{children}</a>;
};

const TopBanner = ({ banner, onClose }) => (
  <div
    className="relative w-full text-[13px] hk-fade-up"
    style={{ backgroundColor: banner.bg_color, color: banner.text_color }}
    data-testid="promo-banner-top"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-2.5 flex flex-wrap items-center justify-center md:justify-between gap-3">
      <div className="flex items-center gap-3 flex-wrap justify-center">
        {banner.emoji && <span className="text-[15px]">{banner.emoji}</span>}
        <span className="font-medium tracking-wide">{banner.title}</span>
        {banner.subtitle && <span className="opacity-80 hidden sm:inline">{banner.subtitle}</span>}
        {banner.price_label && (
          <span
            className="px-2 py-0.5 rounded-full text-[11px] font-medium tracking-widest"
            style={{ backgroundColor: banner.accent_color, color: banner.bg_color }}
          >
            {banner.price_label}
          </span>
        )}
        {banner.countdown_to && <CountdownPill target={banner.countdown_to} textColor={banner.text_color} />}
      </div>
      <div className="flex items-center gap-3">
        <CtaLink
          href={banner.cta_href}
          testId="promo-banner-cta"
          className="px-4 py-1.5 rounded-full text-[12px] font-medium tracking-widest uppercase hk-pulse"
          style={{ backgroundColor: banner.accent_color, color: banner.bg_color }}
        >
          {banner.cta_label}
        </CtaLink>
        <button onClick={onClose} data-testid="promo-banner-close" aria-label="Dismiss" className="opacity-70 hover:opacity-100">
          <X size={16} />
        </button>
      </div>
    </div>
  </div>
);

const BottomBanner = ({ banner, onClose }) => (
  <div className="fixed left-3 right-3 bottom-3 md:left-6 md:right-6 md:bottom-6 z-50 pointer-events-none">
    <div
      className="pointer-events-auto mx-auto max-w-3xl rounded-[16px] shadow-[0_18px_50px_-12px_rgba(30,35,31,0.35)] hk-fade-up flex items-center gap-4 px-4 md:px-5 py-3.5"
      style={{ backgroundColor: banner.bg_color, color: banner.text_color }}
      data-testid="promo-banner-bottom"
    >
      <div className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center hk-pulse"
        style={{ backgroundColor: banner.accent_color, color: banner.bg_color }}>
        <Flame size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {banner.emoji && <span>{banner.emoji}</span>}
          <span className="font-medium tracking-wide text-[13.5px] truncate">{banner.title}</span>
          {banner.price_label && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-medium tracking-widest"
              style={{ backgroundColor: banner.accent_color, color: banner.bg_color }}
            >
              {banner.price_label}
            </span>
          )}
        </div>
        {banner.subtitle && <div className="text-[12px] opacity-80 mt-0.5 truncate">{banner.subtitle}</div>}
        {banner.countdown_to && <div className="mt-1"><CountdownPill target={banner.countdown_to} textColor={banner.text_color} /></div>}
      </div>
      <CtaLink
        href={banner.cta_href}
        testId="promo-banner-cta"
        className="px-4 py-2 rounded-full text-[12px] font-medium tracking-widest uppercase shrink-0"
        style={{ backgroundColor: banner.accent_color, color: banner.bg_color }}
      >
        {banner.cta_label}
      </CtaLink>
      <button onClick={onClose} data-testid="promo-banner-close" aria-label="Dismiss" className="shrink-0 opacity-70 hover:opacity-100">
        <X size={16} />
      </button>
    </div>
  </div>
);

const SideBanner = ({ banner, onClose }) => (
  <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden md:block">
    <div
      className="rounded-l-[14px] shadow-[0_10px_30px_-8px_rgba(30,35,31,0.35)] px-4 py-4 flex flex-col items-center gap-2 hk-fade-up"
      style={{ backgroundColor: banner.bg_color, color: banner.text_color, writingMode: "horizontal-tb" }}
      data-testid="promo-banner-side"
    >
      {banner.emoji && <span className="text-[18px]">{banner.emoji}</span>}
      <div className="text-[11px] tracking-[0.2em] uppercase" style={{ color: banner.accent_color }}>
        {banner.price_label || "Offer"}
      </div>
      <div className="serif text-[15px] leading-tight max-w-[140px] text-center">{banner.title}</div>
      <CtaLink
        href={banner.cta_href}
        testId="promo-banner-cta"
        className="mt-1 px-3 py-1.5 rounded-full text-[11px] font-medium tracking-widest uppercase hk-pulse"
        style={{ backgroundColor: banner.accent_color, color: banner.bg_color }}
      >
        {banner.cta_label}
      </CtaLink>
      <button onClick={onClose} data-testid="promo-banner-close" aria-label="Dismiss" className="mt-1 opacity-60 hover:opacity-100">
        <X size={12} />
      </button>
    </div>
  </div>
);

export const GlobalPromoBanner = () => {
  const { banner, isDismissed, dismiss } = useBanner();
  const loc = useLocation();

  if (!banner || isDismissed) return null;
  // Hide on admin routes
  if (loc.pathname.startsWith("/admin")) return null;
  // Page filter
  const pages = banner.pages || ["*"];
  if (!pages.includes("*") && !pages.includes(loc.pathname)) return null;

  const style = banner.style || "top";
  if (style === "bottom") return <BottomBanner banner={banner} onClose={dismiss} />;
  if (style === "side") return <SideBanner banner={banner} onClose={dismiss} />;
  return <TopBanner banner={banner} onClose={dismiss} />;
};

export default GlobalPromoBanner;
